import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  schemaJson?: object;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://www.crownglobalfactoryandbuyer.com";
const DEFAULT_OG_IMAGE = "https://public.readdy.ai/ai/img_res/c2709c1283b7effd5be5b17a3173c628.jpg";

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  schemaJson,
}: SEOProps) {
  useEffect(() => {
    // Title
    document.title = title;

    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // Core meta
    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);

    // Last-Modified
    setMeta("last-modified", new Date().toISOString().split("T")[0]);

    // Robots — all pages using useSEO are indexable
    setMeta("robots", "index, follow");

    // Canonical
    const canonicalHref = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
    setLink("canonical", canonicalHref);

    // Open Graph
    setMeta("og:title", ogTitle || title, "property");
    setMeta("og:description", ogDescription || description, "property");
    setMeta("og:url", canonicalHref, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:image:width", "1200", "property");
    setMeta("og:image:height", "630", "property");
    setMeta("og:image:alt", ogTitle || title, "property");
    setMeta("og:site_name", "Crown Global Factory & Buyer Platform", "property");
    setMeta("og:locale", "en_US", "property");

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", ogTitle || title);
    setMeta("twitter:description", ogDescription || description);
    setMeta("twitter:image", ogImage);
    setMeta("twitter:image:alt", ogTitle || title);
    setMeta("twitter:site", "@CrownGlobalTrade");

    // Schema.org JSON-LD
    const schemaId = "page-schema-jsonld";
    let schemaEl = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (schemaJson) {
      if (!schemaEl) {
        schemaEl = document.createElement("script");
        schemaEl.id = schemaId;
        schemaEl.setAttribute("type", "application/ld+json");
        document.head.appendChild(schemaEl);
      }
      schemaEl.textContent = JSON.stringify(schemaJson);
    } else if (schemaEl) {
      schemaEl.remove();
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogImage, schemaJson]);
}

export { SITE_URL };
